'use client'

import { Check, X } from 'lucide-react'

interface ComparisonItem {
  type: 'distillates' | 'isolates' | 'live-resin'
  title: string
  description: string
  isPositive: boolean
}

interface ProductComparisonProps {
  title?: string
  subtitle?: string
  description?: string
  items?: ComparisonItem[]
  className?: string
}

export default function ProductComparison({
  title = "Why Live Resin Outshines Distillates and Isolates",
  subtitle = "More impact, less product",
  description = "Live resin captures the full essence of the plant, delivering authentic flavor, potent effects, and cost-effective efficiency that distillates and isolates can't match.",
  items = [],
  className = ''
}: ProductComparisonProps) {
  return (
    <section className={`py-20 section-bg-dark relative overflow-hidden ${className}`}>
      {/* Background Images */}
      <div className="absolute inset-0">
        <div className="absolute left-0 top-0 w-1/3 h-full opacity-20">
          <img 
            src="/images/61f5cc833f7bdaff84f4b6f7_sour-space-candy.jpg" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-20">
          <img 
            src="/images/Copy-of-Copy-of-crude.png" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-eskerium-dark via-eskerium-dark/95 to-eskerium-dark"></div>
      </div>
      
      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8">
            <div>
              <div className="brand-chip mb-4">
                {subtitle}
              </div>
              
              <h2 className="heading-lg text-hierarchy-1 mb-6">
                Why <span className="italic-accent">Live Resin</span> {title.split('Live Resin')[1]}
              </h2>
              
              <p className="text-lg text-hierarchy-2 leading-relaxed mb-8">
                {description}
              </p>
            </div>
            
            {/* Comparison Items */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-eskerium-gold mb-4">
                A True-to-plant Experience
              </h3>
              
              {items.map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    item.isPositive 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {item.isPositive ? <Check size={16} /> : <X size={16} />}
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-hierarchy-1 mb-2">
                      {item.title}
                    </h4>
                    <p className="text-hierarchy-2 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right Side - Visual Comparison */}
          <div className="space-y-8">
            <div className="glass-card p-8 text-center">
              <h3 className="text-2xl font-semibold text-hierarchy-1 mb-6">
                The Clear Choice in Purity, Flavor, Aroma and Experience
              </h3>
              
              <div className="space-y-6">
                <div className="relative">
                  <img 
                    src="/images/61f5cc833f7bdaff84f4b6f7_sour-space-candy.jpg" 
                    alt="Our Full-Spectrum Live Resin" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <div className="absolute bottom-4 left-4 bg-eskerium-gold text-eskerium-dark px-3 py-1 rounded-full text-sm font-semibold">
                    Our Full-Spectrum Live Resin
                  </div>
                </div>
                
                <div className="relative">
                  <img 
                    src="/images/Copy-of-Copy-of-crude.png" 
                    alt="Crude Full-Spectrum Oil" 
                    className="w-full h-48 object-cover rounded-lg opacity-60"
                  />
                  <div className="absolute bottom-4 left-4 bg-gray-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Crude Full-Spectrum Oil
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-eskerium-gold/10 rounded-lg border border-eskerium-gold/20">
                <p className="text-sm text-hierarchy-2">
                  <strong className="text-eskerium-gold">The Live Resin Difference:</strong><br/>
                  Enhanced terpene and cannabinoid concentration offers a stronger, more targeted impact, maximizing the entourage experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}